<template>
  <div class="min-h-screen bg-gray-900 text-gray-100 font-sans relative overflow-x-hidden flex flex-col">
    <!-- Background Elements -->
    <div class="fixed inset-0 z-0">
      <div
        class="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1541447271487-09612b3f49f7?q=80&w=2069&auto=format&fit=crop')] bg-cover bg-center opacity-20">
      </div>
      <div class="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-900/90 to-red-900/20"></div>
    </div>

    <!-- Navigation -->
    <nav
      class="fixed top-0 w-full z-50 transition-all duration-300 bg-black/30 backdrop-blur-md border-b border-white/5">
      <div class="container mx-auto px-4">
        <div class="flex items-center justify-between h-20">
          <div class="flex items-center">
            <router-link to="/" class="flex items-center group">
              <span class="text-3xl font-bold text-white tracking-tighter" style="font-family: 'Oswald', sans-serif;">
                <span class="text-red-600 mr-1 italic">F1</span>Gp
              </span>
            </router-link>

            <div class="hidden md:block ml-12">
              <div class="flex items-baseline space-x-1">
                <router-link v-for="link in links" :key="link.path" :to="link.path"
                  class="px-4 py-2 rounded-lg text-sm font-medium text-gray-300 transition-all duration-200 hover:text-white hover:bg-white/10"
                  active-class="bg-red-600/90 text-white shadow-lg shadow-red-900/50">
                  {{ link.name }}
                </router-link>
              </div>
            </div>
          </div>

          <!-- Right Side Actions -->
          <div class="flex items-center space-x-3">
            <!-- Install App Button (Visible if deferredPrompt exists and toast closed) -->
            <button v-if="deferredPrompt && !showInstallToast" @click="installApp"
              class="flex items-center justify-center p-2 md:px-3 md:py-1.5 text-gray-300 md:text-white md:bg-red-600 md:hover:bg-red-700 hover:text-white hover:bg-white/10 rounded-full md:rounded-lg transition shadow-none md:shadow-lg"
              title="Installa App">
              <i class="fas fa-download md:mr-2"></i> <span class="hidden md:inline">Installa</span>
            </button>

            <!-- Share Button -->
            <button @click="shareApp"
              class="p-2 text-gray-300 hover:text-white hover:bg-white/10 rounded-full transition" title="Condividi">
              <i class="fas fa-share-alt"></i>
            </button>

            <!-- Mobile Menu Button -->
            <div class="md:hidden">
              <button @click="toggleMobileMenu" class="text-gray-300 hover:text-white focus:outline-none p-2"
                aria-label="Toggle menu">
                <i class="fas" :class="isMobileMenuOpen ? 'fa-times' : 'fa-bars'"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Mobile Menu -->
      <transition name="fade">
        <div v-if="isMobileMenuOpen"
          class="md:hidden bg-gray-900/95 backdrop-blur-xl border-b border-white/10 absolute w-full left-0 top-20 shadow-2xl z-40">
          <div class="px-4 pt-2 pb-6 space-y-2">
            <router-link v-for="link in links" :key="link.path" :to="link.path"
              class="block px-4 py-3 rounded-lg text-base font-medium text-gray-300 hover:text-white hover:bg-white/10 transition"
              active-class="bg-red-600/20 text-red-400 border-l-4 border-red-600 pl-3" @click="toggleMobileMenu">
              {{ link.name }}
            </router-link>
            <!-- Mobile Install Button -->
            <button v-if="deferredPrompt" @click="installApp"
              class="w-full text-left px-4 py-3 rounded-lg text-base font-medium text-red-500 hover:bg-white/10 transition flex items-center">
              <i class="fas fa-download mr-3"></i> Installa App
            </button>
          </div>
        </div>
      </transition>
    </nav>

    <!-- Main Content -->
    <main class="relative z-10 pt-24 pb-12 flex-1">
      <router-view v-slot="{ Component }">
        <transition name="fade" mode="out-in">
          <component :is="Component" />
        </transition>
      </router-view>
    </main>

    <!-- Footer -->
    <footer class="relative z-10 border-t border-white/10 bg-black/60 backdrop-blur-md py-6 mt-auto">
      <div class="container mx-auto px-4 text-center">
        <p class="text-gray-400 text-sm font-medium">
          &copy; 2026 - <span class="text-white font-bold tracking-widest uppercase italic"><span
              class="text-red-600">F1</span>Gp</span> -
          <a href="https://www.gabriarcu.it" target="_blank" rel="noopener noreferrer"
            class="text-red-500 hover:text-red-400 border-b border-transparent hover:border-red-400 transition-all">Gabriarcu.it</a>
        </p>
      </div>
    </footer>

    <!-- Update Toast Notification -->
    <transition name="slide-up">
      <div v-if="showUpdateToast"
        class="fixed bottom-4 left-4 right-4 md:left-auto md:right-8 md:w-96 bg-gray-900 border border-red-600/50 rounded-xl shadow-2xl p-4 z-[110] flex items-center justify-between backdrop-blur-md">
        <div class="flex items-center">
          <div class="bg-red-600/20 p-2 rounded-lg mr-3 text-red-500">
            <i class="fas fa-arrow-up-right-from-square"></i>
          </div>
          <div>
            <h4 class="font-bold text-white text-sm">Nuova versione disponibile!</h4>
            <p class="text-xs text-gray-400">Clicca qui per aggiornare l'app</p>
          </div>
        </div>
        <div class="flex items-center space-x-2">
          <button @click="updateApp"
            class="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-xs font-bold rounded-lg transition whitespace-nowrap">
            AGGIORNA
          </button>
          <button @click="showUpdateToast = false" class="p-1.5 text-gray-400 hover:text-white transition">
            <i class="fas fa-times"></i>
          </button>
        </div>
      </div>
    </transition>

    <!-- Install Toast Notification -->
    <transition name="slide-up">
      <div v-if="showInstallToast"
        class="fixed bottom-4 left-4 right-4 md:left-auto md:right-8 md:w-96 bg-gray-900 border border-red-600/30 rounded-xl shadow-2xl p-4 z-[100] flex items-center justify-between backdrop-blur-md">
        <div class="flex items-center">
          <div class="bg-red-600/20 p-2 rounded-lg mr-3 text-red-500">
            <i class="fas fa-mobile-screen-button text-xl"></i>
          </div>
          <div>
            <h4 class="font-bold text-white text-sm">Installa F1Gp</h4>
            <p class="text-xs text-gray-400">Accedi rapidamente dalla tua Home</p>
          </div>
        </div>
        <div class="flex items-center space-x-2">
          <button @click="installApp"
            class="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-xs font-bold rounded-lg transition">
            INSTALLA
          </button>
          <button @click="dismissToast" class="p-1.5 text-gray-400 hover:text-white transition">
            <i class="fas fa-times"></i>
          </button>
        </div>
      </div>
    </transition>

  </div>
</template>

<script>
import { ref, onMounted, onBeforeUnmount } from 'vue';

export default {
  setup() {
    const isMobileMenuOpen = ref(false);
    const deferredPrompt = ref(null);
    const showInstallToast = ref(false);

    // PWA Update State
    const showUpdateToast = ref(false);
    const swRegistration = ref(null);

    let toastTimer = null;

    const links = [
      { name: 'Home', path: '/' },
      { name: 'Gare', path: '/races' },
      { name: 'Classifiche', path: '/standings' },
      { name: 'Piloti', path: '/drivers' },
      { name: 'Teams', path: '/teams' },
      // { name: 'Telemetria', path: '/live-telemetry' },
      { name: 'Confronto', path: '/compare' },
      { name: 'Stats', path: '/stats' },
      { name: 'WorldGp', path: '/world-gp' },
    ];

    const toggleMobileMenu = () => {
      isMobileMenuOpen.value = !isMobileMenuOpen.value;
    };

    const dismissToast = () => {
      showInstallToast.value = false;
      if (toastTimer) clearTimeout(toastTimer);
    };

    const installApp = async () => {
      dismissToast();
      if (!deferredPrompt.value) return;

      deferredPrompt.value.prompt();
      const { outcome } = await deferredPrompt.value.userChoice;
      console.log(`User response to the install prompt: ${outcome}`);
      deferredPrompt.value = null;
    };

    const shareApp = async () => {
      if (navigator.share) {
        try {
          await navigator.share({
            title: 'F1Gp - Formula 1 Live Companion',
            text: 'Segui il campionato di Formula 1 2026: risultati, classifiche, piloti e circuiti in tempo reale.',
            url: window.location.href,
          });
        } catch (err) {
          console.error('Error sharing:', err);
        }
      } else {
        // Fallback or simple alert
        alert('Copia il link per condividere: ' + window.location.href);
      }
    };

    const updateApp = () => {
      showUpdateToast.value = false;
      if (swRegistration.value && swRegistration.value.waiting) {
        // Manda il segnale per dire al SW di attivarsi
        swRegistration.value.waiting.postMessage({ type: 'SKIP_WAITING' });
      }
    };

    onMounted(() => {
      // Listen for SW updates
      window.addEventListener('swUpdated', (e) => {
        swRegistration.value = e.detail;
        showUpdateToast.value = true;
      });

      window.addEventListener('beforeinstallprompt', (e) => {
        // Prevent Chrome 67 and earlier from automatically showing the prompt
        e.preventDefault();
        // Stash the event so it can be triggered later.
        deferredPrompt.value = e;

        // Show toast
        showInstallToast.value = true;

        // Auto hide after 10 seconds
        toastTimer = setTimeout(() => {
          showInstallToast.value = false;
        }, 10000);
      });
    });

    onBeforeUnmount(() => {
      if (toastTimer) clearTimeout(toastTimer);
    });

    return {
      links,
      isMobileMenuOpen,
      toggleMobileMenu,
      deferredPrompt,
      showInstallToast,
      showUpdateToast,
      installApp,
      dismissToast,
      shareApp,
      updateApp
    };
  }
}
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=Oswald:wght@400;500;700&display=swap');

body {
  font-family: 'Inter', sans-serif;
}

h1,
h2,
h3,
h4,
h5,
h6 {
  font-family: 'Oswald', sans-serif;
}

/* Transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-up-enter-from,
.slide-up-leave-to {
  opacity: 0;
  transform: translateY(20px);
}

/* Scrollbar */
::-webkit-scrollbar {
  width: 8px;
}

::-webkit-scrollbar-track {
  background: #0f172a;
}

::-webkit-scrollbar-thumb {
  background: #334155;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: #475569;
}
</style>
